import React from "react";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaLayerGroup,
  FaUsers,
  FaChalkboardTeacher,
  FaArrowRight,
  FaUser,
} from "react-icons/fa";

function AvatarImage({ src, alt }) {
  return (
    <img
      src={src || "/img/no-image.jpg"}
      alt={alt}
      onError={(e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = "/img/no-image.jpg";
      }}
      className="h-16 w-16 rounded-2xl object-cover border border-slate-200 dark:border-slate-700 bg-white"
    />
  );
}

function Pill({ icon: Icon, children, tone = "slate" }) {
  const tones = {
    slate:
      "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    blue: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
    emerald:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    violet:
      "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
    amber:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    rose:
      "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  };

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${tones[tone] || tones.slate}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}

const ClubsGrid = ({ clubs, onSelectClub }) => {
  if (!clubs.length) {
    return (
      <div className="rounded-[28px] border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-10 text-center shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          Aucun club trouvé
        </h3>
        <p className="text-sm text-slate-500 mt-2">
          Essaie de modifier la recherche ou les filtres.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {clubs.map((club) => {
        const userLabel = club.user
          ? `${club.user.nom || ""} ${club.user.prenom || ""}`.trim()
          : "Aucun utilisateur";

        return (
          <div
            key={club.id}
            className="rounded-[28px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden"
          >
            <div className="p-5 md:p-6">
              <div className="flex items-start gap-4">
                <AvatarImage src={club.logo_url} alt={club.nom_club} />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                        {club.nom_club || "Club sans nom"}
                      </h3>

                      <div className="mt-2 space-y-1 text-sm text-slate-500 dark:text-slate-400">
                        <div className="flex items-start gap-2">
                          <FaUser className="mt-0.5 shrink-0" />
                          <span>{userLabel}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <FaMapMarkerAlt className="mt-0.5 shrink-0" />
                          <span>{club.adresse_club || "Adresse non renseignée"}</span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => onSelectClub(club.id)}
                      className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 transition"
                    >
                      Ouvrir
                      <FaArrowRight />
                    </button>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Pill icon={FaBuilding} tone="blue">
                      Département : {club.departement_club || "—"}
                    </Pill>
                    <Pill icon={FaMapMarkerAlt} tone="emerald">
                      {club.totalDojos || 0} dojo{club.totalDojos > 1 ? "s" : ""}
                    </Pill>
                    <Pill icon={FaLayerGroup} tone="violet">
                      {club.totalGroups || 0} groupe{club.totalGroups > 1 ? "s" : ""}
                    </Pill>
                    <Pill icon={FaUsers} tone="amber">
                      {club.totalAdherents || 0} adhérent
                      {club.totalAdherents > 1 ? "s" : ""}
                    </Pill>
                    <Pill icon={FaChalkboardTeacher} tone="rose">
                      {club.teachersCount || 0} enseignant
                      {club.teachersCount > 1 ? "s" : ""}
                    </Pill>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ClubsGrid;